<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            (isAdd ? $t("actions.add") : $t("actions.edit")) +
            " " +
            $tc("pacom.sites", 1)
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>
      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Master account -->
          <validation-provider
            #default="validationContext"
            name="id_pacom_master_account"
            rules="required"
          >
            <b-form-group
              :label="$t('pacom.masterAccount')"
              label-for="id_pacom_master_account"
              :state="getValidationState(validationContext)"
            >
              <v-select
                label="username"
                v-model="itemData.id_pacom_master_account"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="masterAccounts"
                :clearable="false"
                :reduce="(val) => val.id"
                @input="(val) => fetchResources(val)"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Name -->
          <validation-provider
            #default="validationContext"
            name="name"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pacom.name')"
              label-for="firstName"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Description -->
          <validation-provider
            #default="validationContext"
            name="description"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pacom.description')"
              label-for="description"
            >
              <b-form-input
                id="description"
                v-model="itemData.description"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Address -->
          <validation-provider
            #default="validationContext"
            name="address"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pacom.address')"
              label-for="address"
            >
              <b-form-input
                id="address"
                v-model="itemData.address"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Timezone -->
          <validation-provider
            #default="validationContext"
            name="timezone"
            rules="required"
          >
            <b-form-group
              label="Time zone"
              label-for="timezone"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.time_zone"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="timezoneOptions"
                :clearable="false"
                :reduce="(val) => val.label"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Unit -->
          <validation-provider #default="validationContext" name="Unit">
            <b-form-group
              class="text-capitalize"
              :label="$tc('unit.title', 2)"
              label-for="id_unit"
              :state="getValidationState(validationContext)"
            >
              <select-paginate
                v-model="itemData.id_unit"
                :items="unitOptions"
                input-id="id_unit"
                :multiple="true"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Center -->
          <validation-provider #default="validationContext" name="Center">
            <b-form-group
              class="text-capitalize"
              :label="$tc('centers.title', 1)"
              label-for="id_center"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_center"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="centerOptions"
                :reduce="(val) => val.value"
                input-id="id_center"
                @input="changeCenter"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Sector -->
          <validation-provider #default="validationContext" name="Sector">
            <b-form-group
              label="Sector"
              label-for="id_sector"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_sector"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sectorOptions"
                :reduce="(val) => val.value"
                input-id="id_sector"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Calendar -->
          <validation-provider
            #default="validationContext"
            name="calendarId"
            rules="required"
          >
            <b-form-group
              :label="$t('pacom.calendar')"
              label-for="calendarId"
              :state="getValidationState(validationContext)"
            >
              <v-select
                label="name"
                v-model="itemData.calendarId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="calendars"
                :clearable="false"
                :reduce="(calendar) => calendar.id"
                :disabled="disabledSelects"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Profile -->
          <validation-provider
            #default="validationContext"
            name="profileId"
            rules="required"
          >
            <b-form-group
              :label="$t('pacom.profile')"
              label-for="profileId"
              :state="getValidationState(validationContext)"
            >
              <v-select
                label="name"
                v-model="itemData.profileId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="profiles"
                :clearable="false"
                :reduce="(profile) => profile.id"
                :disabled="disabledSelects"
                @input="(val) => fetchProfileDetail(val)"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Gateway -->
          <validation-provider
            #default="validationContext"
            name="gatewayId"
            rules="required"
          >
            <b-form-group
              :label="$t('pacom.serialNumber')"
              label-for="gatewayId"
              :state="getValidationState(validationContext)"
            >
              <v-select
                label="gatewayId"
                v-model="itemData.gatewayId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="gateways"
                :clearable="false"
                :reduce="(gateway) => gateway.gatewayId"
                :disabled="disabledSelects"
                @input="(val) => setGatewayIdEntries(val)"
              >
                <template v-slot:option="option">
                  {{ option.gatewayId }} ({{ option.description }})
                </template>
              </v-select>
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BInputGroupAppend,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { computed, ref } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import SelectPaginate from "../../components/SelectPaginate.vue";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BInputGroupAppend,
    BButton,
    vSelect,
    BFormCheckbox,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
    SelectPaginate
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    tenantOptions: {
      type: Array,
      required: true,
    },
    masterAccounts: {
      type: Array,
      required: true,
    },
    timezoneOptions: {
      type: Array,
      required: true,
    },
    unitOptions: {
      type: Array,
      required: true,
    },
    centerOptions: {
      type: Array,
      required: true,
    },
    sectorOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    const blankData = {
      id_pacom_master_account: null,
      name: null,
      description: null,
      address: null,
      calendarId: null,
      profileId: null,
      gatewayId: null,
      time_zone: null,
      entries: [],
      id_unit: null,
      id_center: null,
      id_sector: null
    };

    const passwordFieldType = ref("password");
    const passwordToggleIcon = computed(() => {
      return passwordFieldType.value === "password" ? "EyeIcon" : "EyeOffIcon";
    });
    const httpSchemaOptions = ["http", "https"];
    const rtspSchemaOptions = ["rtsp", "rtsps"];

    const togglePasswordVisibility = () => {
      passwordFieldType.value =
        passwordFieldType.value === "password" ? "text" : "password";
    };

    const calendars = ref([]);
    const profiles  = ref([]);
    const gateways  = ref([]);

    const toast = useToast();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const disabledSelects = computed(() => !itemData.value.id_pacom_master_account || !props.isAdd);

    const onSubmit = () => {
      let dispatch;

      itemData.value.id_unit = itemData.value.id_unit.map((unit) => unit.value);

      if (props.isAdd) {
        dispatch = store.dispatch("pacom_sites/add", itemData.value);
      } else {
        const req = {
          id: itemData.value.id,
          params: itemData.value,
        };

        dispatch = store.dispatch("pacom_sites/edit", req);
      }

      dispatch
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });

            if (props.isAdd) {
              let { site_id } = response.data;

              (async () => await getSitePacomDetail(site_id, true))();
            }

            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = props.itemEdit;

        (async () => {
            await fetchResources(itemData.value.id_pacom_master_account);
            await getSitePacomDetail(itemData.value.id_pacom_site);
        })();

        itemData.value.id_unit = props.itemEdit.device.units.map((value) => ({
            value: value.id,
            label: value.description,
            id_tenant: value.router.id_tenant,
        }));
      }
    };

    const fetchResources = async (id_pacom_master_account) => {
      let { data: dataCalendars } = await store.dispatch("pacom_master_accounts/getResources", { id_pacom_master_account, resource: 'calendars'});
      let { data: dataProfiles }  = await store.dispatch("pacom_master_accounts/getResources", { id_pacom_master_account, resource: 'profiles'});
      let { data: dataGateways }  = await store.dispatch("pacom_master_accounts/getResources", { id_pacom_master_account, resource: 'gateways'});
      let gatewaysApi = [];

      calendars.value = dataCalendars;
      profiles.value  = dataProfiles;
      gatewaysApi     = dataGateways.split(';');

      gatewaysApi.forEach(gateway => {
          let [gatewayId,description] = gateway.split('=');

          gateways.value.push({
              gatewayId,
              description
          });
      });
    }

    const fetchProfileDetail = async (profileId) => {
      let { data } = await store.dispatch("pacom_master_accounts/getResources", {
        id_pacom_master_account: itemData.value.id_pacom_master_account,
        resource: 'profile_details',
        param: profileId
      });

      itemData.value.entries = [];

      itemData.value.entries = data.entries.map(entrie => {
          return {
            configuration: null,
            deviceTypeId: null,
            gatewayId: null,
            name: null,
            profileEntryId: entrie.id
          }
      });
    }

    const setGatewayIdEntries = (gatewayId) => {
      itemData.value.entries.forEach(entrie => {
        entrie.gatewayId = gatewayId;
      });
    }

    const getSitePacomDetail = async (siteId, createEntries = false) => {
        let { data } = await store.dispatch("pacom_master_accounts/getResources", {
          id_pacom_master_account: itemData.value.id_pacom_master_account,
          resource: 'site_details',
          param: siteId
        });

        if (createEntries) {
            await store.dispatch("pacom_sites/addEntries", {
              entries: data.entries,
              site_id: siteId
            });
        } else {
          itemData.value.entries = data.entries;
        }
    }

    const changeCenter = () => {
      store
        .dispatch("sector/getAll", {
          id_center: [itemData.value.id_center],
        })
        .then(() => {
          const sector = props.sectorOptions.filter(
            (item) => item.value == itemData.value.id_sector
          )

          if (sector.length == 0) itemData.value.id_sector = ""
        })
    }


    return {
      itemData,
      onSubmit,

      refFormObserver,
      getValidationState,
      loadData,
      resetForm,

      passwordFieldType,
      passwordToggleIcon,
      httpSchemaOptions,
      rtspSchemaOptions,
      togglePasswordVisibility,
      fetchResources,
      fetchProfileDetail,
      setGatewayIdEntries,
      changeCenter,

      disabledSelects,
      profiles,
      calendars,
      gateways
    };
  },
};
</script>
